import React, { useState } from 'react';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@jem-open/jem-ui';
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  computeInvoiceGroups,
  ReconProductValue,
  useMultiProductRecon,
} from '../MultiProductReconContext';

export type ModalStep = 'configure' | 'review';

interface CreateMultiProductReconActionsProps {
  step: ModalStep;
  onClose: () => void;
  onNext: () => void;
  onBack: () => void;
  employerId: string;
}

// Context uses lowercase/camelCase; Django DTO uses screaming snake.
const PRODUCT_API_VALUE: Record<ReconProductValue, 'EWA' | 'SAVINGS' | 'JEM_MOBILE'> = {
  ewa: 'EWA',
  savings: 'SAVINGS',
  jemMobile: 'JEM_MOBILE',
};

const toApiProducts = (products: ReconProductValue[]) =>
  products.map((p) => PRODUCT_API_VALUE[p]);

const CreateMultiProductReconActions = ({
  step,
  onClose,
  onNext,
  onBack,
  employerId,
}: CreateMultiProductReconActionsProps) => {
  const { state, isFormValid } = useMultiProductRecon();
  const [submitting, setSubmitting] = useState(false);

  const handleCreate = async () => {
    const invoices = computeInvoiceGroups(
      state.recons,
      state.invoiceStrategy,
      state.customInvoiceGroupByProduct,
    ).map((inv) => ({
      groupNumber: inv.groupNumber,
      reconIndexes: inv.reconIndexes,
      products: toApiProducts(inv.products),
    }));

    const payload = {
      employerId: Number(employerId),
      employerPayCycleIds: state.employerPayCycles.map((pC) => pC.id),
      title: state.title,
      description: state.description,
      startDate: state.schedule.startDate
        ? `${state.schedule.startDate.getFullYear()}-${String(
            state.schedule.startDate.getMonth() + 1,
          ).padStart(2, '0')}-${String(
            state.schedule.startDate.getDate(),
          ).padStart(2, '0')}`
        : null,
      recurrence:
        state.schedule.repeatFrequency === 'weekly'
          ? {
              repeatFrequency: state.schedule.repeatFrequency,
              interval: state.schedule.interval ?? 1,
              dayOfWeek: state.schedule.dayOfWeek,
            }
          : {
              repeatFrequency: state.schedule.repeatFrequency,
              // Monthly recurrence sets interval to null in context; DTO requires int >= 1.
              interval: state.schedule.interval ?? 1,
              monthlyType: state.schedule.monthlyType,
              dateInMonth: state.schedule.dateInMonth,
              nthDayInMonth: state.schedule.nthDayInMonth,
              posDayInMonth: state.schedule.posDayInMonth,
            },
      invoiceStrategy: state.invoiceStrategy,
      invoices,
      attendees: state.attendees,
      recons: state.recons.map((r) => ({
        products: toApiProducts(r.products),
      })),
      xeroContactId: state.xeroContactId,
      xeroInvoiceReference: state.invoiceReference.trim() || null,
    };

    setSubmitting(true);
    try {
      const response = await axios.post('/api/recon_schedules/create', payload);
      if (response.status === 201) {
        onClose();
        await Swal.fire({
          title: 'Multi-Product Recon created',
          icon: 'success',
          toast: true,
          position: 'top',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
        return;
      }
      throw new Error(`Unexpected status ${response.status}`);
    } catch (err: unknown) {
      const axiosErr = axios.isAxiosError(err) ? err : null;
      const status = axiosErr?.response?.status;
      const data = axiosErr?.response?.data as
        | { error?: string; errors?: unknown }
        | undefined;
      const title =
        status === 400
          ? 'Invalid recon schedule'
          : status === 502
            ? 'Calendar event create failed'
            : 'Could not create recon schedule';
      const detail = data?.error
        ? data.error
        : data?.errors
          ? JSON.stringify(data.errors, null, 2)
          : axiosErr?.message ?? 'Unknown error';
      await Swal.fire({
        title,
        html: `<pre style="text-align:left;font-size:11px;max-height:50vh;overflow:auto;white-space:pre-wrap;">${detail}</pre>`,
        icon: 'error',
        confirmButtonText: 'OK',
        width: '700px',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (step === 'configure') {
    const nextButton = (
      <Button
        disabled={!isFormValid}
        onClick={onNext}
        rightIcon={<ArrowRight className="size-4" />}
      >
        Next
      </Button>
    );
    return (
      <>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        {isFormValid ? (
          nextButton
        ) : (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-block">{nextButton}</span>
              </TooltipTrigger>
              <TooltipContent>
                Complete pay cycles, all recons and email recipients to
                continue
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </>
    );
  }

  return (
    <>
      <Button
        variant="outline"
        onClick={onBack}
        disabled={submitting}
        leftIcon={<ArrowLeft className="size-4" />}
      >
        Back
      </Button>
      <Button
        disabled={!isFormValid || submitting}
        onClick={handleCreate}
        leftIcon={submitting ? <Loader2 className="size-4 animate-spin" /> : undefined}
      >
        {submitting ? 'Creating…' : 'Create'}
      </Button>
    </>
  );
};

export default CreateMultiProductReconActions;
